import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {motion, useScroll, useSpring, useTransform} from 'motion/react';
import {useCountDown} from 'ahooks';
import {LeaderboardItemType} from '../types';
import {useDailyBettingBoard} from '../hooks/useDailyBettingBoard';
import {useDailyBettingModals} from '../hooks/useDailyBettingModals';
import Header from '../components/dailyBet/header';
import TabContainer from '../components/dailyBet/tab-container';
import NoticeBar from '@/common-pages/leaderboard/components/dailyBet/notice-bar';
import Jackpot from '@/common-pages/leaderboard/components/dailyBet/jackpot';
import Stage from '@/common-pages/leaderboard/components/dailyBet/stage';
import RankList from '@/common-pages/leaderboard/components/dailyBet/rank-list';
import CurrentUser from '@/common-pages/leaderboard/components/dailyBet/current-user';

// 常量配置
const COUNTDOWN_REFRESH_DELAY = 100; // 倒计时结束后数据刷新延迟(ms)
const SCROLL_THRESHOLD = 5; // 滚动阈值
const PULL_DISTANCE_MAX = -150; // 最大下拉距离
const BOUNCE_DISTANCE_MAX = 50; // 最大回弹距离
const SPRING_CONFIG = {
  stiffness: 400,
  damping: 25,
  mass: 0.8,
};

// 倒计时格式化工具函数
const formatCountdown = (milliseconds: number): string => {
  if (milliseconds <= 0) {
    return '00:00:00';
  }

  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}:${String(seconds).padStart(2, '0')}`;
};

interface DailyBettingBoardProps {
  label?: string;
  currentUser?: LeaderboardItemType;
}

function DailyBettingBoard({
  label = 'Daily Betting Championship',
}: DailyBettingBoardProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'today' | 'yesterday'>('today');
  const [dateOffset, setDateOffset] = useState(0); // 0 = 今天, 1 = 昨天

  // 使用专门的 DailyBettingBoard hook
  const {
    data,
    loading,
    jackpot,
    jackpotLoading,
    currentUser,
    currentUserLoading,
    todayDetail,
    yesterdayRewardInfo,
    refresh,
  } = useDailyBettingBoard(dateOffset);

  // 使用弹窗hook - 传递完整数据
  const {processModals} = useDailyBettingModals(
    yesterdayRewardInfo || undefined,
  );

  // 动态倒计时目标时间 - 优先使用服务端时间
  const targetDate = useMemo(() => {
    // 优先使用服务端时间
    if (todayDetail?.countdownTime) {
      const serverDate = new Date(todayDetail.countdownTime);
      // 验证日期有效性
      if (!isNaN(serverDate.getTime())) {
        return serverDate;
      }
    }

    // 后备本地时间（今天23:59:59）
    const now = new Date();
    const fallback = new Date(now);
    fallback.setHours(23, 59, 59, 999);
    return fallback;
  }, [todayDetail]);

  const handleCountdownEnd = useCallback(() => {
    // 倒计时结束表示已过渡到新的一天，切换到今天的数据
    setDateOffset(0);
    setActiveTab('today'); // 同时切换到今天的tab
    // 延迟刷新确保状态更新完成
    setTimeout(() => {
      refresh();
    }, COUNTDOWN_REFRESH_DELAY);
  }, [refresh]);

  const [countdown] = useCountDown({
    targetDate,
    onEnd: handleCountdownEnd,
  });

  // 倒计时显示格式化 - 基于动态目标时间计算
  const formattedCountdown = useMemo(() => {
    return formatCountdown(countdown);
  }, [countdown]);

  // 回归用户专用的倒计时目标时间
  const regressionTargetDate = useMemo(() => {
    // 如果存在回归用户信息且有回归结束时间，使用回归结束时间
    if (yesterdayRewardInfo?.regressionInfo?.regressionEndDate) {
      const regressionDate = new Date(
        yesterdayRewardInfo.regressionInfo.regressionEndDate,
      );
      // 验证日期有效性
      if (!isNaN(regressionDate.getTime())) {
        return regressionDate;
      }
    }
    // 如果不是回归用户或没有回归结束时间，返回null表示不需要显示回归倒计时
    return null;
  }, [yesterdayRewardInfo]);

  // 回归用户倒计时
  const [regressionCountdown] = useCountDown({
    targetDate: regressionTargetDate,
    onEnd: () => {
      console.log('回归倒计时结束');
    },
  });

  // 回归用户倒计时显示格式化
  const formattedRegressionCountdown = useMemo(() => {
    // 如果没有回归倒计时，返回null
    if (regressionTargetDate === null || regressionCountdown <= 0) {
      return null;
    }

    return formatCountdown(regressionCountdown);
  }, [regressionCountdown, regressionTargetDate]);

  // 初始化时处理弹窗逻辑
  useEffect(() => {
    if (yesterdayRewardInfo) {
      processModals();
    }
  }, [processModals, yesterdayRewardInfo]);

  // 监听滚动进度
  const {scrollY} = useScroll({container: scrollContainerRef});

  // 状态管理 - 根据滚动距离控制元素显示/隐藏
  const [showHiddenItems, setShowHiddenItems] = useState(false);

  // 创建下拉回弹效果 - 当scrollY为负值时生效
  const pullDistance = useTransform(scrollY, value => Math.min(0, value));
  const bounceY = useSpring(
    useTransform(
      pullDistance,
      [PULL_DISTANCE_MAX, 0],
      [BOUNCE_DISTANCE_MAX, 0],
    ),
    SPRING_CONFIG,
  );

  // 优化的滚动处理函数
  const handleScroll = useCallback((value: number) => {
    const shouldShow = value > SCROLL_THRESHOLD;
    setShowHiddenItems(prevState => {
      // 只在状态真正改变时更新
      if (prevState !== shouldShow) {
        return shouldShow;
      }
      return prevState;
    });
  }, []);

  // 使用 useEffect 管理监听器，避免内存泄漏
  useEffect(() => {
    const unsubscribe = scrollY.on('change', handleScroll);
    return () => {
      unsubscribe();
    };
  }, [scrollY, handleScroll]);

  // 处理 tab 切换
  const handleTabChange = useCallback((tab: 'today' | 'yesterday') => {
    setActiveTab(tab);
    // 根据 tab 切换日期
    const offset = tab === 'today' ? 0 : 1; // 'today' = 今天, 'yesterday' = 昨天
    setDateOffset(offset);
  }, []);
  const noticeText = useMemo(() => {
    return (
      <div className="flex gap-6">
        <span>{todayDetail?.showText1}</span>
        <span>{todayDetail?.showText2}</span>
      </div>
    );
  }, [todayDetail]);

  return (
    <div className="relative daily-bg h-screen flex flex-col overflow-hidden translate-x-0 bg-black">
      <Header label={label} />
      <TabContainer onTabChange={handleTabChange} activeTab={activeTab} />
      {noticeText && activeTab === 'today' && <NoticeBar text={noticeText} />}
      <Jackpot
        countdown={formattedCountdown}
        jackpot={jackpot || 0}
        loading={jackpotLoading}
        showCountdown={activeTab === 'today'}
      />
      <motion.div
        ref={scrollContainerRef}
        style={{y: bounceY}}
        role="region"
        className="flex-1 overflow-y-auto touch-pan-y pb-28 relative pt-3 mt-1"
        aria-label="Leaderboard scrollable content">
        <motion.div
          initial={false}
          animate={showHiddenItems ? {opacity: 0} : {opacity: 1}}
          transition={{
            opacity: {
              duration: 0.1,
              ease: 'easeInOut',
            },
          }}
          className="top-3 left-0 absolute w-full">
          <Stage topThree={data.slice(0, 3)} loading={loading} />
        </motion.div>
        <motion.div
          key="rank-list-3" // 强制重新挂载，确保动画重新触发
          initial={{
            opacity: 0,
            scale: 0,
          }}
          animate={
            showHiddenItems
              ? {
                  opacity: 1,
                  scale: 1,
                  transition: {
                    opacity: {duration: 0.1},
                    scale: {duration: 0.2},
                  },
                }
              : {
                  opacity: 0,
                  scale: 0,
                  transition: {
                    opacity: {duration: 0.1},
                    scale: {duration: 0.2},
                  },
                }
          }
          style={{
            transformOrigin: 'top center',
          }}
          className="overflow-hidden pb-2">
          <div className="pt-[10px]">
            <RankList data={data.slice(0, 3)} loading={loading} />
          </div>
        </motion.div>
        <RankList data={data.slice(3)} loading={loading} />
      </motion.div>

      <CurrentUser
        countdown={formattedRegressionCountdown}
        currentUser={currentUser}
        loading={currentUserLoading}
        yesterdayRewardInfo={yesterdayRewardInfo}
      />
    </div>
  );
}

export default DailyBettingBoard;
